'use client'

import ConfirmModal from '@/components/confirm-modal'
import GenerateBillingModal from '@/components/generate-billing-modal'
import ActionsMenu, { ActionMenuItem } from '@/components/actions-menu'
import ProntuarioMenu from '@/components/prontuario/prontuario-menu'
import CancelAppointmentModal from '@/components/medical/cancel-appointment-modal'
import CancellationNotice from '@/components/medical/cancellation-notice'
import TransferSellerModal from '@/components/medical/transfer-seller-modal'
import ScheduleAppointmentModal from '@/components/scheduling/schedule-appointment-modal'
import ChangeStatusModal from '@/components/bookings/ChangeStatusModal'
import {
    approveExamBudget,
    renewExamGuide,
    cancelExam,
    transferExamSeller,
    generateExamBilling,
    deleteExam,
    Exam,
    getExamStatusColor,
    getExamStatusLabel,
    exportExamBudgetPdf,
    getExam,
    markExamBillingAsPaid,
    cancelExamBilling,
    printExamResults,
    printExamOrder,
    sendExamBudgetEmail,
    changeExamStatus,
    EXAM_STATUS_LABELS,
    updateExamServiceAddress,
    type ExamStatus,

} from '@/lib/api/exams/exam-api'
import { PdfDelivery } from '@/lib/download-blob'
import { GuideValidityLine } from '@/components/guide-validity-line'
import { computeRenewedGuideValidUntil, useGuideValidityDays } from '@/lib/guide-validity'
import { useAuth } from '@/store/hooks'
import ApproveBudgetModal from '@/components/service-address/approve-budget-modal'
import ChangeServiceAddressModal from '@/components/service-address/change-service-address-modal'
import ServiceAddressCard from '@/components/service-address/service-address-card'
import { ServiceAddress } from '@/components/service-address/service-address-section'
import BillingInfoSection from '@/components/billing/BillingInfoSection'
import BillingChildrenSections from '@/components/billing/billing-children-sections'
import { BillingInfoData } from '@/components/billing/BillingInfoSection'
import { SignDocumentModal } from '@/components/signature/SignDocumentModal'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useContext, useEffect, useState } from 'react'
import { SocketContext } from '@/lib/providers/socket-provider'
import PresenceTimeline from '@/components/presence/presence-timeline'
import {
    MdArrowBack,
    MdManageAccounts,
    MdAssignment,
    MdAttachFile,
    MdBiotech,
    MdBusiness,
    MdLocalHospital,
    MdCalendarToday,
    MdCheck,
    MdCancel,
    MdDelete,
    MdEdit,
    MdSwapHoriz,
    MdReceiptLong,
    MdMedicalServices,
    MdOpenInNew,
    MdPerson,
    MdPictureAsPdf,
    MdDownload,
    MdPrint,
    MdEmail,
    MdEvent,
    MdEventRepeat,
    MdShoppingCart,
    MdCheckCircle,
    MdTrendingUp,
    MdMeetingRoom,
    MdFlag,
    MdLocationOn,

} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import {
    isSharedBilling,
    moneyOrDash,
    netRevenue,
    profitMargin,
    resolveCost,
    resolveProfit,
} from '@/lib/billing/financials'
import {
    resolveAppointmentBillingState,
    type AppointmentAnchorLike,
} from '@/lib/billing/appointment-billing-state'
import { UserType } from '@/lib/api/common/interfaces/user.interface'
import { toast } from 'react-toastify'

interface ExamDetailPageProps {
    /** Renderiza embutido (modal) para este id, ignorando a rota. */
    idOverride?: number
    /** Substitui o "voltar para a lista" por fechar o modal. */
    onBack?: () => void
}

export default function ExamDetailPage({ idOverride, onBack }: ExamDetailPageProps = {}) {
    const navigate = useNavigate()
    const { user } = useAuth()
    const guideValidityDays = useGuideValidityDays(user?.companyId)
    const { canUpdate, canDelete, canCreate } = useResourcePermissions('bookings', 'exams')
    const { ClientLabel } = useClientLabel()
    const { id: paramId } = useParams<{ id: string }>()
    const id = idOverride ? String(idOverride) : paramId
    const { socket, isConnected } = useContext(SocketContext)

    // Pagamento confirmado pelo gateway: o webhook invalida o cache DESTE
    // atendimento e emite este evento. Sem escutar aqui, a cobrança seguia
    // aparecendo como pendente na tela até o operador recarregar.
    useEffect(() => {
        if (!socket || !isConnected || !id) return
        const eventName = `appointment-billing-exam-${id}`
        const handle = () => loadExam()
        socket.on(eventName, handle)
        return () => {
            socket.off(eventName, handle)
        }
    }, [socket, isConnected, id])
    /** No modal, sair da lista significa fechar; na rota, navegar. */
    const goBack = () => (onBack ? onBack() : navigate('/bookings/exams'))
    const [exam, setExam] = useState<Exam | null>(null)
    const [loading, setLoading] = useState(true)
    const [deleting, setDeleting] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [cancelling, setCancelling] = useState(false)
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [showScheduleModal, setShowScheduleModal] = useState(false)
    const [showStatusModal, setShowStatusModal] = useState(false)
    const [savingStatus, setSavingStatus] = useState(false)
    const [transferring, setTransferring] = useState(false)
    const [showTransferModal, setShowTransferModal] = useState(false)
    const [printing, setPrinting] = useState(false)
    const [showSignModal, setShowSignModal] = useState(false)
    const [printingOrder, setPrintingOrder] = useState<boolean>(false)
    const [showSignOrderModal, setShowSignOrderModal] = useState<boolean>(false)
    const [showApproveModal, setShowApproveModal] = useState(false)
    const [showAddressModal, setShowAddressModal] = useState(false)
    const [savingAddress, setSavingAddress] = useState(false)
    const [approvingBudget, setApprovingBudget] = useState(false)
    const [printingBudget, setPrintingBudget] = useState(false)
    const [sendingBudgetEmail, setSendingBudgetEmail] = useState(false)

    useEffect(() => {
        if (id) {
            loadExam()
        }
    }, [id])

    const loadExam = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getExam(parseInt(id, 10))
            if (response.success && response.result) {
                setExam(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar exame')
                goBack()
            }
        } catch (error) {
            toast.error('Erro ao carregar exame')
            goBack()
        } finally {
            setLoading(false)
        }
    }

    /**
     * Cancelar não é excluir: o exame permanece na listagem como "Cancelado".
     * Se a cobrança estava paga, o backend responde `refundPending` — o dinheiro só
     * volta depois de um operador financeiro aprovar o estorno, e o usuário precisa
     * saber disso agora, não quando estranhar que o cliente não foi reembolsado.
     */
    const confirmCancel = async (reason: string) => {
        if (!id || !exam) return

        setCancelling(true)
        try {
            const response = await cancelExam(parseInt(id, 10), reason)
            if (response.success) {
                if (response.result?.refundPending) {
                    toast.success(
                        'Exame cancelado. A solicitação de estorno foi enviada para aprovação de um operador financeiro.',
                    )
                } else {
                    toast.success('Exame cancelado com sucesso!')
                }
                setShowCancelModal(false)
                await loadExam()
            } else {
                toast.error(response.error || 'Erro ao cancelar exame')
            }
        } catch (error) {
            toast.error('Erro ao cancelar exame')
        } finally {
            setCancelling(false)
        }
    }

    // Transferir a venda (createdBy) para outro usuário. A autorização real é do backend
    // (só COMPANY ou o vendedor atual); aqui a ação nem aparece para quem não pode.
    const confirmTransferSeller = async (sellerId: number) => {
        if (!id || !exam) return
        setTransferring(true)
        try {
            const response = await transferExamSeller(parseInt(id, 10), sellerId)
            if (response.success) {
                toast.success('Vendedor transferido com sucesso!')
                setShowTransferModal(false)
                await loadExam()
            } else {
                toast.error(response.error || 'Erro ao transferir vendedor')
            }
        } catch {
            toast.error('Erro ao transferir vendedor')
        } finally {
            setTransferring(false)
        }
    }

    // Gerar cobrança para um exame que ficou sem cobrança. Após criar, vai direto à cobrança.
    const [generatingBilling, setGeneratingBilling] = useState(false)
    const [showGenerateBillingModal, setShowGenerateBillingModal] =
        useState(false)
    /**
     * `amount` ausente = cobra o saldo inteiro. O valor vem daqui, e não da tela de
     * edição da cobrança, porque alterar o valor de uma cobrança atrelada é proibido
     * — é por ali que sairia desconto sem autorização.
     */
    const handleGenerateBilling = async (amount?: number) => {
        if (!id || !exam) return
        setGeneratingBilling(true)
        try {
            const response = await generateExamBilling(parseInt(id, 10), amount)
            if (response.success && response.result?.billingId) {
                toast.success('Cobrança gerada com sucesso!')
                setShowGenerateBillingModal(false)
                // Vai para a EDIÇÃO (escolher forma de pagamento, vencimento etc.), com
                // `from=schedule` para exibir cliente/parceiro herdados como no agendamento.
                navigate(
                    `/financial/billings/${response.result.billingId}/edit?from=schedule`,
                )
            } else {
                toast.error(response.error || 'Erro ao gerar cobrança')
            }
        } catch {
            toast.error('Erro ao gerar cobrança')
        } finally {
            setGeneratingBilling(false)
        }
    }

    const [showRenewGuideModal, setShowRenewGuideModal] = useState(false)
    const [renewingGuide, setRenewingGuide] = useState(false)
    const handleRenewGuide = async () => {
        if (!id) return
        setRenewingGuide(true)
        try {
            const response = await renewExamGuide(parseInt(id, 10))
            if (response.success) {
                toast.success('Guia renovada com sucesso')
                setShowRenewGuideModal(false)
                await loadExam()
            } else {
                toast.error(response.error || 'Erro ao renovar a guia')
            }
        } catch {
            toast.error('Erro ao renovar a guia')
        } finally {
            setRenewingGuide(false)
        }
    }

    const confirmDelete = async () => {
        if (!id || !exam) return

        setDeleting(true)
        try {
            const response = await deleteExam(parseInt(id, 10))
            if (response.success) {
                toast.success('Exame excluído com sucesso!')
                goBack()
            } else {
                toast.error(response.error || 'Erro ao excluir exame')
            }
        } catch (error) {
            toast.error('Erro ao excluir exame')
        } finally {
            setDeleting(false)
            setShowDeleteModal(false)
        }
    }

    /**
     * `delivery` decide entre abrir a impressão e salvar o arquivo. "Imprimir"
     * abre o diálogo direto — o operador quer o papel, não um PDF na pasta de
     * downloads; "Baixar PDF" fica como ação separada no menu.
     */
    const handlePrint = async (delivery: PdfDelivery = 'print') => {
        if (!exam) return
        setPrinting(true)
        try {
            await printExamResults(exam.id, delivery)
            if (delivery === 'download') toast.success('PDF gerado com sucesso!')
        } catch (error) {
            toast.error('Erro ao gerar PDF')
        } finally {
            setPrinting(false)
        }
    }

    const handlePrintOrder = async (delivery: PdfDelivery = 'print') => {
        if (!exam) return
        setPrintingOrder(true)
        try {
            if (exam.status === 'BUDGET') {
                await exportExamBudgetPdf(exam.id, delivery)
            } else {
                // Gera no backend: resolve a logo como data URI (sem CORS) e monta
                // o endereço do parceiro — a versão client-side não fazia ambos.
                await printExamOrder(exam.id, delivery)
            }
            if (delivery === 'download') toast.success('PDF gerado com sucesso!')
        } catch (error) {
            toast.error('Erro ao gerar PDF')
        } finally {
            setPrintingOrder(false)
        }
    }

    const isBudget = exam?.status === 'BUDGET'
    const isExpired = isBudget && exam?.validUntil && new Date(exam.validUntil) < new Date()

    // Estado financeiro do exame (âncora + complementos). Só cobrança VIVA bloqueia a
    // geração de uma nova: cancelada, estornada ou baixada como perda deixa o exame sem
    // dívida de pé, e antes o operador ficava sem como cobrar.
    // `shouldBecomeAnchor` mantém a ação visível mesmo com valor 0, para que o backend
    // responda "defina um valor no agendamento" em vez de o item sumir do menu.
    const billingState = resolveAppointmentBillingState(
        (exam as any)?.billing as AppointmentAnchorLike | null | undefined,
        exam?.finalPrice ?? 0
    )
    const canGenerateBilling =
        billingState.shouldBecomeAnchor || billingState.canGenerate

    /**
     * Custo/lucro são informação sensível: atendente ou perfil de parceiro não vê o
     * repasse. Liberado para a empresa e para cargos administrativos — que na prática
     * são identificados pela permissão de exclusão do recurso.
     *
     * Os valores vêm de snapshot congelado na venda (`Billing` no total, `ExamItem.unitCost`
     * por item), nunca do catálogo, que é editável. Ver `@/lib/billing/financials`.
     */
    const canSeeCosts = user?.userType === UserType.COMPANY || canDelete
    const showCostBreakdown = canSeeCosts && !!exam?.billing
    /**
     * A cobrança pode cobrir vários atendimentos (um pagamento só para a visita
     * inteira). Nesse caso custo, venda e lucro DESTA página são os da fatia deste
     * exame — sem informar a origem, viriam os valores dos três juntos, ou "—",
     * porque os campos escalares da cobrança ficam nulos. Ver
     * docs/billing-payout-share.md.
     */
    const examSource = exam ? { sourceType: 'EXAM', sourceId: exam.id } : null
    const examCost = resolveCost(exam?.billing, examSource)
    const examProfit = resolveProfit(exam?.billing, examSource)
    const examNetRevenue = netRevenue(exam?.billing, examSource)
    const billingIsShared = isSharedBilling(exam?.billing)
    const sharedCount = exam?.billing?.payoutShares?.length ?? 0

    /**
     * Exame coberto por convênio: a cobrança nasce faturada à operadora, sem
     * link nem forma de pagamento a escolher. Ver docs/health-insurance-system.md §2.
     */
    const examCoveredByHealthPlan = !!(exam as any)?.healthPlanId

    /** O endereço confirmado no modal vira o local de atendimento do exame. */
    const handleApproveBudget = async (
        address: ServiceAddress,
        payment?: { method: string; installments: number | null }
    ) => {
        if (!exam) return
        setApprovingBudget(true)
        try {
            const response = await approveExamBudget(exam.id, {
                ...address,
                // Forma fechada na aprovação: a cobrança nasce completa e o link
                // já pode ir, sem passar pela tela de cobrança.
                ...(payment
                    ? {
                          paymentMethod: payment.method,
                          installmentCount: payment.installments ?? undefined,
                      }
                    : {}),
            } as any)
            if (response.success) {
                toast.success('Orçamento aprovado com sucesso!')
                const billingId = (response.result as any)?.billingId
                // Desvia só quando a cobrança nasceu SEM método — era a única coisa
                // que faltava definir lá.
                // Cobrança de convênio não tem forma de pagamento a definir
                // (sem gateway, sem link): a tela de cobrança não teria o que
                // resolver. Ver docs/health-insurance-system.md §2.
                if (billingId && !payment && !examCoveredByHealthPlan) {
                    navigate(`/financial/billings/${billingId}/edit?from=budget`)
                } else {
                    loadExam()
                }
            } else {
                toast.error(response.error || 'Erro ao aprovar orçamento')
            }
        } catch {
            toast.error('Erro ao aprovar orçamento')
        } finally {
            setApprovingBudget(false)
            setShowApproveModal(false)
        }
    }

    /**
     * Troca o local de atendimento já gravado. Endpoint próprio: a edição completa
     * mexeria em preço e cobrança, que nada têm a ver com corrigir o endereço.
     */
    const handleChangeServiceAddress = async (address: ServiceAddress) => {
        if (!exam) return
        setSavingAddress(true)
        try {
            const response = await updateExamServiceAddress(exam.id, address)
            if (response.success) {
                toast.success('Local de atendimento atualizado')
                setShowAddressModal(false)
                await loadExam()
            } else {
                toast.error(
                    response.error || 'Erro ao alterar o local de atendimento'
                )
            }
        } catch {
            toast.error('Erro ao alterar o local de atendimento')
        } finally {
            setSavingAddress(false)
        }
    }

    const handlePrintBudget = async () => {
        if (!exam) return
        setPrintingBudget(true)
        try {
            await exportExamBudgetPdf(exam.id)
            toast.success('PDF do orçamento gerado com sucesso!')
        } catch {
            toast.error('Erro ao gerar PDF do orçamento')
        } finally {
            setPrintingBudget(false)
        }
    }

    const handleSendBudgetEmail = async () => {
        if (!exam) return
        setSendingBudgetEmail(true)
        try {
            const response = await sendExamBudgetEmail(exam.id)
            if (response.success) {
                toast.success('Orçamento enviado por email com sucesso!')
            } else {
                toast.error(response.error || 'Erro ao enviar orçamento')
            }
        } catch {
            toast.error('Erro ao enviar orçamento por email')
        } finally {
            setSendingBudgetEmail(false)
        }
    }

    /**
     * Troca manual de status. Cancelado fica fora das opções: cancelar mexe em
     * cobrança, estorno e agenda, e tem ação própria (o backend recusa por aqui).
     */
    /**
     * Voltar para ORÇAMENTO só enquanto não houver cobrança viva: orçamento é o
     * estado de antes de existir dinheiro, e cobrança em aberto continua pagável
     * pelo link público. O backend recusa de qualquer forma — aqui a opção some
     * para o operador não esbarrar no erro.
     */
    const hasLiveBilling = !!(
        exam?.billing &&
        [
            exam.billing,
            ...((exam.billing as any).adjustments ?? []),
        ].some((b: any) => b?.status !== 'CANCELLED')
    )

    const statusOptions = (
        Object.entries(EXAM_STATUS_LABELS) as [string, string][]
    )
        .filter(([value]) => value !== 'CANCELLED')
        .filter(([value]) => value !== 'BUDGET' || !hasLiveBilling)
        .map(([value, label]) => ({ value, label }))

    const handleChangeStatus = async (status: string) => {
        setSavingStatus(true)
        try {
            const res = await changeExamStatus(exam!.id, status as ExamStatus)
            if (res.success) {
                toast.success('Status alterado.')
                setShowStatusModal(false)
                await loadExam()
            } else {
                toast.error(res.error || 'Não foi possível alterar o status.')
            }
        } finally {
            setSavingStatus(false)
        }
    }

    /**
     * Ações secundárias do cabeçalho. A primária de cada estado (aprovar, num
     * orçamento) fica fora do menu — aqui vai o que não é o motivo da visita.
     *
     * Montada antes dos early-returns de loading/404, então `exam` pode ser null:
     * o menu só é renderizado depois deles.
     */
    const examActions: ActionMenuItem[] = exam
        ? [
            ...(!isBudget
                ? [{
                    key: 'export-pdf',
                    label: 'Exportar PDF',
                    icon: <MdPictureAsPdf />,
                    onClick: () => setShowSignOrderModal(true),
                    title: 'Exportar PDF com assinatura digital',
                }]
                : []),
            {
                key: 'print',
                label: isBudget ? 'Imprimir orçamento' : 'Imprimir',
                icon: <MdPrint />,
                onClick: () => handlePrintOrder('print'),
                loading: printingOrder,
            },
            {
                key: 'download',
                label: isBudget ? 'Baixar orçamento' : 'Baixar PDF',
                icon: <MdDownload />,
                onClick: () => handlePrintOrder('download'),
                loading: printingOrder,
            },
            ...(isBudget && canUpdate
                ? [{
                    key: 'budget-email',
                    label: 'Enviar por e-mail',
                    icon: <MdEmail />,
                    onClick: handleSendBudgetEmail,
                    loading: sendingBudgetEmail,
                }]
                : []),
            // Exame cancelado não é editável (o backend recusa): editar o valor geraria
            // cobrança complementar ou estorno para um atendimento que não vai acontecer.
            // Marcar/remarcar a data sem atravessar a tela de edição, que carrega o
            // atendimento inteiro para quem só precisa dizer "é quinta às 14h".
            // Orçamento fica de fora: data é coisa de atendimento aprovado.
            ...(canUpdate && exam.status !== 'CANCELLED' && exam.status !== 'BUDGET'
                ? [{
                    key: 'schedule',
                    label: exam.scheduledAt ? 'Remarcar' : 'Agendar',
                    icon: <MdEvent />,
                    onClick: () => setShowScheduleModal(true),
                }]
                : []),
            // O local é confirmado uma vez (agendamento ou aprovação do orçamento) e
            // depois disso não havia como mexer: a tela de edição não mostra o campo.
            // Em orçamento fica de fora — ali o local ainda não existe.
            ...(canUpdate && exam.status !== 'CANCELLED' && !isBudget
                ? [{
                    key: 'change-service-address',
                    label: 'Alterar endereço de atendimento',
                    icon: <MdLocationOn />,
                    onClick: () => setShowAddressModal(true),
                }]
                : []),
            // Régua do operador: a importação trouxe atendimento marcado como
            // concluído sem ter acontecido, e não havia como corrigir.
            ...(canUpdate && exam.status !== 'CANCELLED'
                ? [{
                    key: 'change-status',
                    label: 'Alterar status',
                    icon: <MdFlag />,
                    onClick: () => setShowStatusModal(true),
                }]
                : []),
            ...(canUpdate && exam.status !== 'CANCELLED'
                ? [{
                    key: 'edit',
                    label: 'Editar',
                    icon: <MdEdit />,
                    onClick: () =>
                        navigate(`/atendimento/scheduling/${exam.id}?type=EXAM`),
                }]
                : []),
            // Transferir vendedor: só o vendedor atual (createdBy) ou um administrador
            // (userType COMPANY). O backend reforça; aqui a ação nem aparece para os demais.
            ...((user?.userType === UserType.COMPANY ||
                user?.id === exam.createdBy?.id) &&
                exam.status !== 'CANCELLED'
                ? [{
                    key: 'transfer-seller',
                    label: 'Transferir vendedor',
                    icon: <MdSwapHoriz />,
                    onClick: () => setShowTransferModal(true),
                }]
                : []),
            // Gerar cobrança: enquanto as cobranças VIVAS não fecharem o valor total, e
            // não é orçamento (o backend recusa nos dois casos). Resolve o agendamento
            // que não gerou cobrança automaticamente e o que teve a cobrança cancelada
            // ou estornada.
            ...(canUpdate && canGenerateBilling && !isBudget && exam.status !== 'CANCELLED'
                ? [{
                    key: 'generate-billing',
                    label: 'Gerar cobrança',
                    icon: <MdReceiptLong />,
                    /**
                     * Só a COMPLEMENTAR pergunta o valor. A primeira cobrança do exame
                     * tem de cobrir o total (o backend recusa menos): quem segura o
                     * repasse até o cliente fechar a conta só existe para procedimento,
                     * e uma primeira parcial pagaria o parceiro pelo valor cheio.
                     */
                    onClick: () =>
                        billingState.shouldBecomeAnchor
                            ? handleGenerateBilling()
                            : setShowGenerateBillingModal(true),
                    loading: generatingBilling,
                }]
                : []),
            // Renovar guia: só em atendimento (orçamento tem validade própria). Redefine
            // o vencimento da guia para hoje + dias configurados em "Validade das Guias".
            ...(canUpdate && !isBudget && exam.status !== 'CANCELLED'
                ? [{
                    key: 'renew-guide',
                    label: 'Renovar guia',
                    icon: <MdEventRepeat />,
                    onClick: () => setShowRenewGuideModal(true),
                    loading: renewingGuide,
                }]
                : []),
            // Cancelar preserva o registro; excluir apaga. São ações distintas, e
            // ambas ficam abaixo do divisor, com rótulo — antes eram dois ícones
            // sem texto colados no botão de editar.
            // Concluído TAMBÉM pode ser cancelado — ver nota na consulta.
            ...(canUpdate && exam.status !== 'CANCELLED'
                ? [{
                    key: 'cancel',
                    label: exam.packageCatalogId ? 'Cancelar pacote' : 'Cancelar exame',
                    icon: <MdCancel />,
                    onClick: () => setShowCancelModal(true),
                    danger: true,
                    loading: cancelling,
                }]
                : []),
            ...(canDelete
                ? [{
                    key: 'delete',
                    label: 'Excluir',
                    icon: <MdDelete />,
                    onClick: () => setShowDeleteModal(true),
                    danger: true,
                    loading: deleting,
                }]
                : []),
        ]
        : []

    const totalResults = exam?.results?.length || 0
    const totalItems = exam?.items?.length || 0
    const itemsWithResults = exam?.items?.filter(item =>
        exam.results?.some(r => r.catalogId === item.catalogId)
    ).length || 0

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando detalhes do exame...
                </p>
            </div>
        )
    }

    if (!exam) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                    <MdBiotech className="text-4xl text-base-content/30" />
                </div>
                <div className="text-center">
                    <h3 className="text-lg font-semibold text-base-content mb-1">
                        Exame não encontrado
                    </h3>
                    <p className="text-sm text-base-content/60">
                        O exame que você está procurando não existe
                    </p>
                </div>
                <button
                    onClick={() => goBack()}
                    className="btn btn-secondary"
                >
                    Voltar para Exames
                </button>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => goBack()}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 hidden md:flex rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 items-center justify-center shadow-lg">
                            <MdBiotech className="text-2xl text-white" />
                        </div>
                        <div>
                            <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                                {exam.packageCatalogId != null
                                    ? exam.title
                                    : exam.items && exam.items.length > 0
                                      ? exam.items.length === 1
                                          ? (exam.items[0].catalog?.name || 'Item sem catálogo')
                                          : `${exam.items[0].catalog?.name || 'Item sem catálogo'} +${exam.items.length - 1}`
                                      : exam.title}
                            </h1>
                            <p className="text-sm text-base-content/60">
                                {exam.packageCatalogId != null
                                    ? 'Detalhes do pacote'
                                    : 'Detalhes do pedido de exames'}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {/* Aprovar é o que a pessoa veio fazer num orçamento: fica em
                        evidência, fora do menu. */}
                    {isBudget && canUpdate && (
                        <button
                            onClick={() => setShowApproveModal(true)}
                            disabled={approvingBudget}
                            className="btn btn-success btn-sm gap-2"
                            title="Aprovar Orçamento"
                        >
                            {approvingBudget ? (
                                <span className="loading loading-spinner loading-sm"></span>
                            ) : (
                                <MdCheckCircle className="text-lg" />
                            )}
                            <span className="hidden sm:inline">Aprovar</span>
                        </button>
                    )}
                    {/* Prontuário do paciente sem sair do atendimento (só no segmento saúde). */}
                    <ProntuarioMenu
                        clientId={exam.client?.id}
                        clientName={exam.client?.name}
                    />
                    <ActionsMenu items={examActions} />
                </div>
            </div>

            {exam.status === 'CANCELLED' && (
                <CancellationNotice
                    entityLabel={exam.packageCatalogId ? 'pacote' : 'exame'}
                    cancelledAt={exam.cancelledAt}
                    cancellationReason={exam.cancellationReason}
                />
            )}

            {/* Info Card */}
            <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span
                                className={`badge ${getExamStatusColor(
                                    exam.status,
                                    exam.scheduledAt
                                )} text-white`}
                            >
                                {getExamStatusLabel(
                                    exam.status,
                                    exam.scheduledAt
                                )}
                            </span>
                            {isBudget && isExpired && (
                                <span className="badge badge-error text-white">Expirado</span>
                            )}
                            {isBudget && exam.validUntil && !isExpired && (
                                <span className="text-xs text-base-content/60">
                                    Válido até {format(new Date(exam.validUntil), 'dd/MM/yyyy')}
                                </span>
                            )}
                            {exam.approvedAt && exam.approver && (
                                <span className="text-xs text-success">
                                    Aprovado em {format(new Date(exam.approvedAt), 'dd/MM/yyyy')} por {exam.approver.name}
                                </span>
                            )}
                        </div>
                        <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-2 text-sm text-base-content/70">
                                <MdCalendarToday />
                                <span>
                                    Solicitado em{' '}
                                    {format(new Date(exam.requestedAt), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                                </span>
                            </div>
                            {(exam.scheduledAt || exam.agendaEvents?.[0]?.startDate) && (
                                <div className="flex items-center gap-2 text-sm text-base-content/70">
                                    <MdCalendarToday />
                                    <span>
                                        Agendado para{' '}
                                        {format(
                                            new Date(exam.scheduledAt || exam.agendaEvents![0].startDate),
                                            "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
                                            { locale: ptBR }
                                        )}
                                    </span>
                                </div>
                            )}
                            <GuideValidityLine issuedAt={exam.createdAt} days={guideValidityDays} guideValidUntil={exam.guideValidUntil} />
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {exam.client && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdPerson className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        {ClientLabel}
                                    </p>
                                    {/* O nome leva ao cadastro do paciente: quem abre o
                                        atendimento quase sempre precisa do histórico, do
                                        contato ou do convênio dele, e antes tinha de sair
                                        e buscar na listagem de clientes. */}
                                    <button
                                        onClick={() =>
                                            navigate(`/person/clients/${exam.client!.id}`)
                                        }
                                        className="font-semibold link link-hover text-left inline-flex items-center gap-1"
                                        title="Ver cadastro do paciente"
                                    >
                                        {exam.client.name}
                                        <MdOpenInNew className="text-sm text-base-content/40" />
                                    </button>
                                </div>
                            </div>
                        )}

                        {exam.professional && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdMedicalServices className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">Profissional</p>
                                    <p className="font-semibold">{exam.professional.name}</p>
                                    {exam.professional.registrationNumber && (
                                        <p className="text-sm text-base-content/60">
                                            {exam.professional.registrationNumber}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Local de atendimento: fica ao lado do paciente e do
                            profissional porque é o que a recepção precisa dizer ao
                            telefone. Antes só existia no banco. */}
                        {!isBudget && (
                            <ServiceAddressCard
                                address={exam}
                                onChange={
                                    canUpdate && exam.status !== 'CANCELLED'
                                        ? () => setShowAddressModal(true)
                                        : undefined
                                }
                            />
                        )}

                        {exam.createdBy && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-info/10 rounded-lg">
                                    <MdManageAccounts className="text-xl text-info" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">Agendado por</p>
                                    <p className="font-semibold">{exam.createdBy.name}</p>
                                    {exam.createdBy.email && (
                                        <p className="text-sm text-base-content/60">
                                            {exam.createdBy.email}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {exam.partner && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-accent/10 rounded-lg">
                                    <MdBusiness className="text-xl text-accent" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">Parceiro</p>
                                    <p className="font-semibold">{exam.partner.name}</p>
                                    {exam.partner.tradingName && (
                                        <p className="text-sm text-base-content/60">
                                            {exam.partner.tradingName}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                        {exam.hospital && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-error/10 rounded-lg">
                                    <MdLocalHospital className="text-xl text-error" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">Hospital</p>
                                    <p className="font-semibold">
                                        {exam.hospital.tradingName || exam.hospital.name}
                                    </p>
                                    {exam.hospital.tradingName &&
                                        exam.hospital.name !== exam.hospital.tradingName && (
                                            <p className="text-sm text-base-content/60">
                                                {exam.hospital.name}
                                            </p>
                                        )}
                                    {exam.hospital.cnpj && (
                                        <p className="text-xs text-base-content/50 mt-1">
                                            CNPJ: {exam.hospital.cnpj}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                        {exam.healthPlan && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-info/10 rounded-lg">
                                    <MdBusiness className="text-xl text-info" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-base-content/60 mb-1">Convênio</p>
                                    <p className="font-semibold">{exam.healthPlan.name}</p>
                                    {exam.healthPlan.ansCode && (
                                        <p className="text-xs text-base-content/50">
                                            ANS: {exam.healthPlan.ansCode}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                        {(() => {
                            const roomData = (exam as any).roomRef || (exam as any).agendaEvents?.[0]?.room
                            if (!roomData) return null
                            return (
                                <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                    <div className="p-2 bg-secondary/10 rounded-lg">
                                        <MdMeetingRoom className="text-xl text-secondary" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-base-content/60 mb-1">Sala / Consultório</p>
                                        <p className="font-semibold">{roomData.name}</p>
                                        {roomData.description && (
                                            <p className="text-xs text-base-content/60">{roomData.description}</p>
                                        )}
                                    </div>
                                </div>
                            )
                        })()}
                    </div>

                    <PresenceTimeline kind="EXAM" appointmentId={exam.id} />

                    {exam.description && (
                        <div className="mb-6">
                            <h3 className="text-lg font-bold text-base-content mb-3">Descrição</h3>
                            <div className="p-4 bg-base-200/50 rounded-xl">
                                <p className="text-base-content whitespace-pre-wrap">{exam.description}</p>
                            </div>
                        </div>
                    )}

                    {exam.notes && (
                        <div className="mb-6">
                            <h3 className="text-lg font-bold text-base-content mb-3">Observações</h3>
                            <div className="p-4 bg-base-200/50 rounded-xl">
                                <p className="text-base-content whitespace-pre-wrap">{exam.notes}</p>
                            </div>
                        </div>
                    )}

                    {exam.completedAt && (
                        <div className="flex items-center gap-2 text-sm text-success">
                            <MdCalendarToday />
                            <span>
                                Concluído em{' '}
                                {format(new Date(exam.completedAt), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Items Card */}
            {exam.items && exam.items.length > 0 && (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                        <div className="flex items-center gap-2">
                            <MdShoppingCart className="text-xl text-secondary" />
                            <h3 className="text-lg font-bold text-base-content">
                                {exam.packageCatalogId != null ? 'Componentes do Pacote' : 'Exames Solicitados'}
                            </h3>
                            <span className="badge badge-secondary badge-sm">{exam.items.length}</span>
                        </div>
                        {exam.packageCatalogId != null && (
                            <p className="text-xs text-base-content/60 mt-1">
                                Os valores por item são apenas referência (guia/TISS). Cobra-se o
                                preço único do pacote.
                            </p>
                        )}
                    </div>

                    <div className="p-6">
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>Tipo</th>
                                        <th className="text-right">Preço Unit.</th>
                                        <th className="text-center">Qtd.</th>
                                        <th className="text-right">Subtotal</th>
                                        {canSeeCosts && (
                                            <>
                                                <th className="text-right">Custo</th>
                                                <th className="text-right">Lucro</th>
                                            </>
                                        )}
                                        <th className="text-center">Resultado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {exam.items.map((item, index) => {
                                        const hasResult = exam.results?.some(r => r.catalogId === item.catalogId)
                                        // Venda e custo do item já multiplicados pela quantidade.
                                        // `unitCost` nulo = item anterior ao snapshot → "—".
                                        const itemSale = item.unitPrice * item.quantity
                                        const itemCost =
                                            item.unitCost == null ? null : item.unitCost * item.quantity
                                        const itemProfit = itemCost == null ? null : itemSale - itemCost
                                        return (
                                            <tr key={item.id || index}>
                                                <td className="font-medium">{item.catalog?.name || 'Item sem catálogo'}</td>
                                                <td>
                                                    {item.catalog.catalogType && (
                                                        <span className="badge badge-sm">
                                                            {item.catalog.catalogType.name}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-right">R$ {(item.unitPrice / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                <td className="text-center">{item.quantity}</td>
                                                <td className="text-right font-medium">R$ {(itemSale / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                {canSeeCosts && (
                                                    <>
                                                        <td className="text-right text-warning">
                                                            {moneyOrDash(itemCost)}
                                                        </td>
                                                        <td
                                                            className={`text-right font-medium ${
                                                                itemProfit != null && itemProfit < 0
                                                                    ? 'text-error'
                                                                    : 'text-success'
                                                            }`}
                                                        >
                                                            {moneyOrDash(itemProfit)}
                                                        </td>
                                                    </>
                                                )}
                                                <td className="text-center">
                                                    {hasResult ? (
                                                        <MdCheck className="text-success text-lg mx-auto" />
                                                    ) : (
                                                        <span className="text-base-content/30">-</span>
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex flex-col items-end gap-2 mt-4 pt-4 border-t border-base-300/50">
                            {exam.totalPrice != null && exam.totalPrice > 0 && (
                                <div className="flex items-center gap-4">
                                    <span className="text-sm text-base-content/70">Subtotal:</span>
                                    <span className="font-medium">R$ {(exam.totalPrice / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                            )}
                            {exam.discount != null && exam.discount > 0 && (
                                <div className="flex items-center gap-4">
                                    <span className="text-sm text-base-content/70">Desconto:</span>
                                    <span className="font-medium text-accent">{exam.discount}%</span>
                                </div>
                            )}
                            {(exam as any).couponCode && (
                                <div className="flex items-center gap-4">
                                    <span className="text-sm text-base-content/70">
                                        Cupom {(exam as any).couponCode}:
                                    </span>
                                    <span className="font-medium text-success">
                                        -R$ {(((exam as any).couponDiscount ?? 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                            )}
                            {exam.finalPrice != null && exam.finalPrice > 0 && (
                                <div className="flex items-center gap-4">
                                    <span className="text-sm font-semibold">Valor Final:</span>
                                    <span className="text-lg font-bold text-secondary">
                                        R$ {(exam.finalPrice / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                            )}

                            {/* Custo e lucro só para a empresa (não para atendente/parceiro). */}
                            {showCostBreakdown && (
                                <div className="w-full mt-3 pt-3 border-t border-base-300/50 flex flex-col items-end gap-2">
                                    <div className="flex items-center gap-4">
                                        <span className="text-sm text-base-content/70">Custo:</span>
                                        <span className="font-medium text-warning">
                                            {moneyOrDash(examCost)}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="text-sm text-base-content/70">
                                            Venda (líquida):
                                        </span>
                                        <span className="font-medium">
                                            {moneyOrDash(examNetRevenue)}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="text-sm font-semibold">Lucro:</span>
                                        <span
                                            className={`text-lg font-bold ${
                                                examProfit != null && examProfit < 0
                                                    ? 'text-error'
                                                    : 'text-success'
                                            }`}
                                        >
                                            {moneyOrDash(examProfit)}
                                        </span>
                                        {/* Percentual do lucro é a MARGEM (sobre a
                                            venda), nunca o markup (sobre o custo). */}
                                        {examNetRevenue != null &&
                                            examProfit != null &&
                                            profitMargin(
                                                examNetRevenue,
                                                examProfit
                                            ) != null && (
                                                <span className="text-xs text-base-content/50">
                                                    (
                                                    {profitMargin(
                                                        examNetRevenue,
                                                        examProfit
                                                    )}
                                                    % de margem)
                                                </span>
                                            )}
                                    </div>
                                    {examCost == null && (
                                        <span className="text-xs text-base-content/50">
                                            Custo não apurado nesta cobrança.
                                        </span>
                                    )}
                                    {/* Cobrança agrupada: sem este aviso, o operador
                                        compara os números daqui com o valor da
                                        cobrança e acha que está errado — a cobrança
                                        vale mais porque cobre outros atendimentos. */}
                                    {billingIsShared && (
                                        <span className="text-right text-xs text-secondary">
                                            Os valores acima são a parte deste exame.
                                            A cobrança é única para {sharedCount}{' '}
                                            atendimentos.
                                        </span>
                                    )}
                                    {/* O total não é a soma das linhas: elas são pré-desconto
                                        e só cobrem repasses por item. */}
                                    <span className="text-xs text-base-content/50 text-right">
                                        O total desconta a taxa de cobrança e inclui hospital e
                                        materiais; as linhas acima são antes do desconto.
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Billing */}
            {exam.billing && (
                <BillingInfoSection
                    // Cobrança pode ser agrupada: o card usa isto para mostrar quanto
                    // do total é deste atendimento.
                    appointmentSource={{ sourceType: 'EXAM', sourceId: exam.id }}
                    billing={exam.billing as BillingInfoData}
                    onMarkAsPaid={async () => {
                        const response = await markExamBillingAsPaid(exam.id)
                        if (response.success) {
                            const result = response.result as any
                            if (result?.pending) {
                                toast.info(
                                    result.message ||
                                        'A solicitação de aprovação para marcar como pago foi enviada'
                                )
                            } else {
                                toast.success('Cobrança marcada como paga!')
                            }
                            loadExam()
                        } else {
                            toast.error(response.error || 'Erro ao marcar como paga')
                        }
                    }}
                    onCancel={async () => {
                        const response = await cancelExamBilling(exam.id)
                        if (response.success) {
                            const result = response.result as any
                            if (result?.pending) {
                                toast.info(
                                    result.message ||
                                        'A solicitação de aprovação para cancelar a cobrança foi enviada'
                                )
                            } else {
                                toast.success('Cobrança cancelada!')
                            }
                            loadExam()
                        } else {
                            toast.error(response.error || 'Erro ao cancelar cobrança')
                        }
                    }}
                    canUpdate={canUpdate}
                    onBillingChanged={loadExam}
                />
            )}

            {/* Complementos/parcelas: cada cobrança filha com o MESMO bloco completo
                (comprovante, marcar como pago etc.). */}
            <BillingChildrenSections
                adjustments={(exam.billing as any)?.adjustments}
                onReload={loadExam}
                canUpdate={canUpdate}
            />

            {/* Deal/Pipeline */}
            {exam.deal && (
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4 flex items-center gap-2">
                            <MdTrendingUp className="text-secondary" />
                            Funil de Vendas
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <span className="text-sm text-base-content/60">Pipeline</span>
                                <p className="font-medium">{exam.deal.pipeline.name}</p>
                            </div>
                            <div>
                                <span className="text-sm text-base-content/60">Etapa</span>
                                <div className="flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: exam.deal.stage.color || '#6b7280' }} />
                                    <span className="font-medium">{exam.deal.stage.name}</span>
                                </div>
                            </div>
                            <div>
                                <span className="text-sm text-base-content/60">Oportunidade</span>
                                <p className="font-medium">{exam.deal.title}</p>
                            </div>
                            <div>
                                <span className="text-sm text-base-content/60">Status</span>
                                <div>
                                    <span className={`badge badge-sm ${exam.deal.status === 'WON' ? 'badge-success' : exam.deal.status === 'LOST' ? 'badge-error' : exam.deal.status === 'ABANDONED' ? 'badge-warning' : 'badge-info'}`}>
                                        {exam.deal.status === 'OPEN' ? 'Aberta' : exam.deal.status === 'WON' ? 'Ganha' : exam.deal.status === 'LOST' ? 'Perdida' : 'Abandonada'}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <span className="text-sm text-base-content/60">Lead</span>
                                <p className="font-medium">{exam.deal.lead.name}</p>
                                {exam.deal.lead.phoneNumber && <p className="text-sm text-base-content/60">{exam.deal.lead.phoneNumber}</p>}
                            </div>
                            {exam.deal.totalValue != null && exam.deal.totalValue > 0 && (
                                <div>
                                    <span className="text-sm text-base-content/60">Valor da Oportunidade</span>
                                    <p className="font-medium text-secondary">{(exam.deal.totalValue / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                                </div>
                            )}
                        </div>
                        <div className="mt-4">
                            <a href={`/sales/opportunities/${exam.deal.id}`} className="btn btn-sm btn-outline btn-secondary">Ver Oportunidade</a>
                        </div>
                    </div>
                </div>
            )}

            {/* Results Card */}
            <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <MdAssignment className="text-xl text-secondary" />
                            <h3 className="text-lg font-bold text-base-content">Resultados</h3>
                            {totalResults > 0 && (
                                <span className="badge badge-success badge-sm text-white">
                                    {itemsWithResults}/{totalItems}
                                </span>
                            )}
                        </div>
                        {totalResults > 0 && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowSignModal(true)}
                                    className="btn btn-outline btn-secondary btn-sm gap-2"
                                    title="Exportar PDF com assinatura digital"
                                >
                                    <MdPictureAsPdf className="text-lg" />
                                    <span className="hidden sm:inline">Exportar PDF</span>
                                </button>
                                <button
                                    onClick={() => handlePrint('print')}
                                    disabled={printing}
                                    className="btn btn-outline btn-sm gap-2"
                                    title="Imprimir com área de assinatura"
                                >
                                    {printing ? (
                                        <span className="loading loading-spinner loading-sm"></span>
                                    ) : (
                                        <MdPrint className="text-lg" />
                                    )}
                                    <span className="hidden sm:inline">Imprimir</span>
                                </button>
                                <button
                                    onClick={() => handlePrint('download')}
                                    disabled={printing}
                                    className="btn btn-outline btn-sm gap-2"
                                    title="Baixar o PDF dos resultados"
                                >
                                    <MdDownload className="text-lg" />
                                    <span className="hidden sm:inline">
                                        Baixar
                                    </span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-6">
                    {totalResults === 0 ? (
                        <div className="text-center py-8">
                            <MdAssignment className="text-4xl mx-auto mb-3 text-base-content/20" />
                            <p className="text-base-content/60 mb-4">Nenhum resultado registrado</p>
                            {canCreate && exam.status !== 'CANCELLED' && (
                                <button
                                    onClick={() => navigate(`/bookings/exams/${exam.id}/resultados`)}
                                    className="btn btn-success gap-2"
                                >
                                    <MdAssignment className="text-lg" />
                                    Registrar Resultados
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {exam.results?.map((result) => (
                                <div
                                    key={result.id}
                                    className="flex items-center gap-4 p-4 bg-base-200/50 rounded-xl border border-base-300/50"
                                >
                                    <div className="p-2 bg-success/10 rounded-lg">
                                        <MdCheck className="text-xl text-success" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-semibold text-base-content">
                                            {result.catalog?.name || 'Resultado'}
                                        </h4>
                                        {result.result && (
                                            <p className="text-sm text-base-content/70 line-clamp-1">
                                                {result.result}
                                            </p>
                                        )}
                                        <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-base-content/50">
                                            {result.professional && (
                                                <span>{result.professional.name}</span>
                                            )}
                                            <span>
                                                {format(new Date(result.resultDate), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                                            </span>
                                            {result.attachments && result.attachments.length > 0 && (
                                                <span className="flex items-center gap-1">
                                                    <MdAttachFile className="text-sm" />
                                                    {result.attachments.length} anexo(s)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <div className="flex justify-center pt-2">
                                <button
                                    onClick={() => navigate(`/bookings/exams/${exam.id}/resultados`)}
                                    className="btn btn-sm btn-secondary gap-2"
                                >
                                    <MdAssignment className="text-base" />
                                    {canCreate && exam.status !== 'CANCELLED'
                                        ? 'Ver / Editar Resultados'
                                        : 'Ver Resultados'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Exam Attachments Card */}
            {exam.attachments && exam.attachments.length > 0 && (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                        <div className="flex items-center gap-2">
                            <MdAttachFile className="text-xl text-secondary" />
                            <h3 className="text-lg font-bold text-base-content">Anexos do Exame</h3>
                            <span className="badge badge-secondary badge-sm">{exam.attachments.length}</span>
                        </div>
                    </div>

                    <div className="p-6">
                        <div className="flex flex-col gap-3">
                            {exam.attachments.map((attachment, index) => (
                                <div key={index} className="bg-base-200/50 rounded-xl border border-base-300/50 p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <MdAttachFile className="text-lg text-secondary" />
                                        <a
                                            href={attachment.filePath}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-semibold text-secondary hover:underline"
                                        >
                                            {attachment.fileName}
                                        </a>
                                    </div>
                                    <div className="space-y-1 text-sm">
                                        <p><span className="font-medium">Usuário:</span> {attachment.userName}</p>
                                        <p><span className="font-medium">Descrição:</span> {attachment.description}</p>
                                        {attachment.details && (
                                            <p><span className="font-medium">Detalhes:</span> {attachment.details}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <CancelAppointmentModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={confirmCancel}
                loading={cancelling}
                entityLabel={exam.packageCatalogId ? 'pacote' : 'exame'}
                billing={exam.billing ?? null}
            />

            {showStatusModal && (
                <ChangeStatusModal
                    entityLabel="exame"
                    currentStatus={exam.status}
                    options={statusOptions}
                    saving={savingStatus}
                    onClose={() => setShowStatusModal(false)}
                    onConfirm={handleChangeStatus}
                />
            )}

            <ScheduleAppointmentModal
                isOpen={showScheduleModal}
                onClose={() => setShowScheduleModal(false)}
                serviceType="EXAM"
                recordId={exam.id}
                currentScheduledAt={exam.scheduledAt}
                currentArrivalOrder={(exam as any).arrivalOrder}
                onScheduled={loadExam}
            />

            <TransferSellerModal
                isOpen={showTransferModal}
                onClose={() => setShowTransferModal(false)}
                onConfirm={confirmTransferSeller}
                loading={transferring}
                currentSellerName={exam.createdBy?.name}
                companyId={exam.companyId}
            />

            <GenerateBillingModal
                isOpen={showGenerateBillingModal}
                onClose={() => setShowGenerateBillingModal(false)}
                onConfirm={handleGenerateBilling}
                chargeable={billingState.remainingToBill}
                allowPartial
                entityLabel="exame"
                loading={generatingBilling}
                appointment={exam ? { type: 'EXAM', id: exam.id } : undefined}
                onOrphanLinked={loadExam}
            />

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title="Excluir exame"
                message="Tem certeza que deseja excluir este exame?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />

            <ConfirmModal
                isOpen={showRenewGuideModal}
                onClose={() => setShowRenewGuideModal(false)}
                onConfirm={handleRenewGuide}
                title="Renovar guia"
                message={`Renovar a validade da guia para ${format(
                    computeRenewedGuideValidUntil(guideValidityDays),
                    "dd 'de' MMMM 'de' yyyy",
                    { locale: ptBR },
                )}?`}
                confirmButtonText="Renovar"
                cancelButtonText="Cancelar"
                loading={renewingGuide}
            />

            <ChangeServiceAddressModal
                isOpen={showAddressModal}
                onClose={() => setShowAddressModal(false)}
                onConfirm={handleChangeServiceAddress}
                loading={savingAddress}
                current={exam}
                partnerId={exam?.partnerId ?? null}
                professionalId={exam?.professionalId ?? null}
                hospitalId={exam?.hospitalId ?? null}
                entityLabel="exame"
            />

            <ApproveBudgetModal
                isOpen={showApproveModal}
                onClose={() => setShowApproveModal(false)}
                onConfirm={handleApproveBudget}
                loading={approvingBudget}
                amount={exam?.finalPrice || exam?.totalPrice || 0}
                healthPlanName={(exam as any)?.healthPlan?.name ?? null}
                partnerId={exam?.partnerId ?? null}
                professionalId={exam?.professionalId ?? null}
                hospitalId={exam?.hospitalId ?? null}
                entityLabel="exame"
            />

            {exam && (
                <SignDocumentModal
                    isOpen={showSignModal}
                    onClose={() => setShowSignModal(false)}
                    documentType="EXAM_RESULTS"
                    documentId={exam.id}
                    documentTitle={`Resultados - ${exam.items?.map(i => (i.catalog?.name || 'Item sem catálogo')).join(', ') || exam.title}`}
                    onSigned={() => {
                        setShowSignModal(false)
                    }}
                />
            )}

            {exam && (
                <SignDocumentModal
                    isOpen={showSignOrderModal}
                    onClose={() => setShowSignOrderModal(false)}
                    documentType="EXAM_ORDER"
                    documentId={exam.id}
                    documentTitle={`Pedido - ${exam.items?.map(i => (i.catalog?.name || 'Item sem catálogo')).join(', ') || exam.title}`}
                    onSigned={() => {
                        setShowSignOrderModal(false)
                    }}
                />
            )}
        </div>
    )
}
